import { Route, Routes, useLocation } from 'react-router-dom';
import AppBar from './components/AppBar';
import ConsentBanner from './components/ConsentBanner';
import SfCookieSync from './components/SfCookieSync';
import SiteFooter from './components/SiteFooter';
import ScreenTracker from './components/ScreenTracker';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './lib/auth/AuthContext';
import { EventStateProvider } from './lib/eventState';
import { notFoundRoute, routes } from './routes';
import { PAGE_META } from './lib/routeMeta';

export default function App() {
  return (
    <AuthProvider>
      <EventStateProvider>
        <AppShell />
      </EventStateProvider>
    </AuthProvider>
  );
}

function AppShell() {
  // /play/game hosts the vendored game in a full-bleed iframe; the SPA
  // chrome (AppBar, footer, vignette, gradient bg, AppBar-height padding)
  // is suppressed there so the iframe owns the viewport. ScrollToTop and
  // ScreenTracker stay mounted — they're hooks-only and don't render chrome.
  const location = useLocation();
  const isGameRoute = location.pathname === PAGE_META.playGame.path;

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-brand-deep text-brand-white">
      {!isGameRoute && (
        <>
          {/*
            Sections own their own backgrounds (Hero → hero_full.png, Competitors
            + /register → players.png, Archive → red gradient, etc.). The shell
            keeps a soft radial vignette over the brand-deep base so placeholder
            pages (Rules / Privacy / Terms / 404 / PrivacySettings) still read as
            part of the same world rather than a bare dark rectangle.
          */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_75%_50%_at_50%_50%,rgba(46,31,60,0)_26%,rgba(15,11,21,1)_100%)] opacity-60"
          />

          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-brand-red focus:px-4 focus:py-2 focus:font-heading focus:uppercase focus:tracking-wider focus:text-white"
          >
            Skip to main content
          </a>
        </>
      )}
      <ScrollToTop />
      <ScreenTracker />
      {!isGameRoute && <AppBar />}
      {/*
        pt offsets the fixed AppBar's height per breakpoint:
          mobile  → 64px (py-3 + 40px hamburger)
          sm–lg   → 72px (py-4 + 40px hamburger)
          lg+     → 68px (py-4 + 36px avatar)
        Keep in sync if AppBar's padding or button sizes change.

        bg gradient is mounted here (not on individual page sections)
        because <main> is the only element guaranteed to span from the
        AppBar bottom to the footer top via flex-1 in this min-h-screen
        column. Page sections with their own opaque background
        (LeaderboardSection's photo, Hero's full-bleed image) cover this
        layer naturally; placeholder + form pages let it show through.
      */}
      <main
        id="main"
        tabIndex={-1}
        className={
          isGameRoute
            ? 'relative flex flex-1 flex-col'
            : 'relative flex flex-1 flex-col bg-gradient-to-b from-[#463e57] to-[#31263d] pt-16 sm:pt-[72px] lg:pt-[68px]'
        }
      >
        <ErrorBoundary>
          <Routes>
            {routes.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path={notFoundRoute.path} element={<notFoundRoute.Component />} />
          </Routes>
        </ErrorBoundary>
      </main>
      {!isGameRoute && <SiteFooter />}
      {!isGameRoute && <ConsentBanner />}
      {!isGameRoute && <SfCookieSync />}
    </div>
  );
}
